import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch } from '../api';
import { formatHours } from '../lib/week';

// A1 (reworked per Tim's 2026-07 trial feedback) — 12-week hours trend,
// stacked per employee. Supervisor-only: the server returns an empty series
// for non-supervisors and this component renders nothing for them
// ("12 week trend is not helpful for employees, just Tim").

interface TrendEmployee {
  id: string;
  fullName: string;
}

interface TrendWeek {
  weekStart: string; // YYYY-MM-DD, Monday in America/Toronto
  minutesByEmployee: Record<string, number>;
}

interface TrendResponse {
  scope: 'all' | 'owned' | 'none';
  employees: TrendEmployee[];
  weeks: TrendWeek[];
}

// Per-employee colors, same brand-blue spectrum + accents the pie uses.
// Assigned by legend order (total hours desc), so the busiest engineer
// keeps the primary brand blue.
const EMPLOYEE_COLORS = [
  '#0079D2', '#26ABE2', '#6366F1', '#14B8A6', '#F97316',
  '#8B5CF6', '#22C55E', '#EAB308', '#0EA5E9', '#94A3B8',
];

// Parse a YYYY-MM-DD into a local Date at noon so the month/day label can't
// slip a day from UTC midnight parsing.
function weekLabel(weekStart: string): string {
  const parts = weekStart.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(
    new Date(y, m - 1, d, 12),
  );
}

type ChartPoint = { label: string } & Record<string, number | string>;

export function TrendChart(): JSX.Element | null {
  const trendQuery = useQuery({
    queryKey: ['dashboard', 'trend'],
    queryFn: () => apiFetch<TrendResponse>('/dashboard/trend'),
    refetchInterval: 30_000,
  });

  const trend = trendQuery.data;

  const data = useMemo<ChartPoint[]>(() => {
    const weeks = trend?.weeks ?? [];
    const emps = trend?.employees ?? [];
    return weeks.map((w) => {
      const point: ChartPoint = { label: weekLabel(w.weekStart) };
      for (const e of emps) {
        point[e.id] = (w.minutesByEmployee[e.id] ?? 0) / 60;
      }
      return point;
    });
  }, [trend]);

  if (trendQuery.isLoading) {
    return (
      <section className="flex flex-col gap-3 pb-2">
        <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">
          12-week trend
        </h2>
        <div className="h-64 w-full animate-pulse rounded-xl bg-brand-steel ring-1 ring-brand-steel" />
      </section>
    );
  }

  // Supervisor-only. Employees get scope 'owned'/'none' with empty data —
  // render nothing rather than an empty chart shell.
  if (!trend || trend.scope !== 'all') return null;

  const nameById = new Map(trend.employees.map((e) => [e.id, e.fullName]));
  const hasData = trend.employees.length > 0;

  return (
    <section className="flex flex-col gap-3 pb-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">
          12-week trend
        </h2>
        <span className="text-xs text-brand-slate">All engineers · hours by employee</span>
      </div>
      <div className="rounded-xl bg-brand-steel p-3 ring-1 ring-brand-steel">
        {!hasData ? (
          <p className="py-8 text-center text-sm text-brand-slate">
            No hours logged in the last 12 weeks.
          </p>
        ) : (
          <>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#D9DFE6" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#5B6776' }}
                    interval="preserveStartEnd"
                    tickLine={false}
                    axisLine={{ stroke: '#D9DFE6' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#5B6776' }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    tickFormatter={(v) => `${v}h`}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const n = typeof value === 'number' ? value : Number(value);
                      const label = nameById.get(String(name)) ?? String(name);
                      return [Number.isFinite(n) ? formatHours(n * 60) : '0h', label];
                    }}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  {trend.employees.map((e, idx) => (
                    <Area
                      key={e.id}
                      type="monotone"
                      dataKey={e.id}
                      stackId="hours"
                      stroke={EMPLOYEE_COLORS[idx % EMPLOYEE_COLORS.length]}
                      fill={EMPLOYEE_COLORS[idx % EMPLOYEE_COLORS.length]}
                      fillOpacity={0.85}
                      strokeWidth={1}
                      isAnimationActive={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
              {trend.employees.map((e, idx) => (
                <li key={e.id} className="flex items-center gap-1.5 text-xs">
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: EMPLOYEE_COLORS[idx % EMPLOYEE_COLORS.length] }}
                  />
                  <span className="text-brand-charcoal">{e.fullName}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
